/**
 * One renderer per block kind. Nothing else in the app renders content.
 *
 * The inline formatter is deliberately tiny — `code`, **bold** and [links](/x) and
 * nothing else. A richer one would mean authors reaching for markdown structure
 * inside a `prose` block instead of using the block that fits, which is exactly
 * the wall of text this site exists to replace.
 */

import { useState, type ReactNode } from 'react';
import type { Accent, Block, Session } from './types.js';

/** `code`, **bold**, [text](href) — one pass, so a match cannot nest into another. */
export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('`')) {
      out.push(<code key={key++}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**')) {
      out.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else {
      const cut = token.indexOf('](');
      out.push(
        <a key={key++} href={token.slice(cut + 2, -1)}>
          {token.slice(1, cut)}
        </a>,
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const slug = (text: string): string =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const accentClass = (a?: Accent): string => (a ? `k-${a}` : 'k-teal');

/**
 * A very small highlighter, tokenising in ONE pass.
 *
 * Single-pass on purpose: a keyword pass followed by a string pass will match the
 * word `class` inside the `class="…"` attribute that the first pass just emitted,
 * and corrupt its own output. One regex with alternation cannot do that.
 */
function highlight(source: string): ReactNode[] {
  const pattern =
    /(\/\/[^\n]*|#[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?\b)|(\b(?:const|let|var|function|async|await|return|import|from|export|if|else|for|new|class|try|catch|throw|def|print|curl|package|func|public|private|struct|val|suspend)\b)|(\b(?:true|false|null|nil|None|undefined)\b)/g;

  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = pattern.exec(source)) !== null) {
    if (m.index > last) out.push(source.slice(last, m.index));
    const cls = m[1] ? 'c-com' : m[2] ? 'c-str' : m[3] ? 'c-num' : m[4] ? 'c-key' : 'c-lit';
    out.push(
      <span key={key++} className={cls}>
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < source.length) out.push(source.slice(last));
  return out;
}

function CodeBlock({ samples }: { samples: { language: string; label?: string; source: string }[] }): ReactNode {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const current = samples[Math.min(active, samples.length - 1)];
  if (!current) return null;

  return (
    <div className="code">
      <div className="bar">
        {samples.length > 1 ? (
          samples.map((s, i) => (
            <button
              key={s.language + String(i)}
              aria-selected={i === active}
              onClick={() => {
                setActive(i);
                setCopied(false);
              }}
            >
              {s.label ?? s.language}
            </button>
          ))
        ) : (
          <span className="t">{current.label ?? current.language}</span>
        )}
        {samples.length > 1 ? <span className="t" /> : null}
        <button
          onClick={() => {
            void navigator.clipboard?.writeText(current.source).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1400);
            });
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
      <pre>{highlight(current.source)}</pre>
    </div>
  );
}

export function BlockView({ block, session }: { block: Block; session: Session | null }): ReactNode {
  switch (block.kind) {
    case 'prose':
      return <p>{inline(block.text)}</p>;

    case 'heading':
      return <h2 id={block.id ?? slug(block.text)}>{block.text}</h2>;

    case 'stats':
      return (
        <div className="grid g4">
          {block.items.map((s, i) => (
            <div key={i} className={`stat ${accentClass(s.accent)}`}>
              <div className="v">{s.value}</div>
              <div className="l">{s.label}</div>
              {s.sub ? <div className="s">{inline(s.sub)}</div> : null}
            </div>
          ))}
        </div>
      );

    case 'flow':
      return (
        <div className={`flow ${accentClass(block.accent)}`}>
          {block.steps.map((s, i) => (
            <div key={i} className="step">
              <div className="i">{s.index ?? String(i + 1).padStart(2, '0')}</div>
              <b>{s.title}</b>
              {s.sub ? <span>{s.sub}</span> : null}
            </div>
          ))}
        </div>
      );

    case 'ladder':
      return (
        <ol className="ladder">
          {block.steps.map((s, i) => (
            <li key={i}>
              <span className="b">{i + 1}</span>
              <b>{s.title}</b>
              <p>{inline(s.text)}</p>
            </li>
          ))}
        </ol>
      );

    case 'cards':
      return (
        <div className={`grid g${block.columns ?? 3}`}>
          {block.items.map((c, i) => {
            const inner = (
              <>
                <span className="top-accent" />
                <h4>{c.title}</h4>
                <p>{inline(c.text)}</p>
                {c.href ? <span className="go">{c.cta ?? 'Read'} →</span> : null}
              </>
            );
            const cls = `card tinted ${accentClass(c.accent)}`;
            return c.href ? (
              <a key={i} className={cls} href={c.href}>
                {inner}
              </a>
            ) : (
              <div key={i} className={cls}>
                {inner}
              </div>
            );
          })}
        </div>
      );

    case 'table':
      return (
        <div className="tbl">
          <div className="scroller">
            <table>
              <thead>
                <tr>
                  {block.columns.map((c, i) => (
                    <th key={i}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{inline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'split':
      return (
        <div className="split">
          {[block.left, block.right].map((pane, i) => (
            <div key={i}>
              <h3>{pane.title}</h3>
              {pane.body.map((b, j) => (
                <BlockView key={j} block={b} session={session} />
              ))}
            </div>
          ))}
        </div>
      );

    case 'callout':
      return (
        <div className={block.tone}>
          <b>{block.title}</b>
          <p>{inline(block.text)}</p>
        </div>
      );

    case 'code':
      return <CodeBlock samples={block.samples} />;

    case 'endpoint':
      return (
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className={`m m-${block.method === 'GET' ? 'get' : block.method === 'POST' ? 'post' : 'del'}`}>
              {block.method}
            </span>
            <code style={{ fontSize: 13.5 }}>{block.path}</code>
            {block.scope ? <span className="pill k-violet">{block.scope}</span> : null}
          </div>
          <p>{inline(block.summary)}</p>
        </div>
      );

    case 'list':
      return block.ordered ? (
        <ol>
          {block.items.map((t, i) => (
            <li key={i}>{inline(t)}</li>
          ))}
        </ol>
      ) : (
        <ul>
          {block.items.map((t, i) => (
            <li key={i}>{inline(t)}</li>
          ))}
        </ul>
      );

    case 'divider':
      return <div style={{ height: 22 }} />;

    case 'reader': {
      // Absent on the business track and before sign-in. Rendering a placeholder
      // would be worse than rendering nothing: it reads like a real value.
      const value = block.field === 'tenant_id' ? session?.tenant_id : session?.display_name;
      if (!value) return null;
      return (
        <div className="stat k-violet" style={{ maxWidth: 380 }}>
          <div className="l">{block.label}</div>
          <div className="v" style={{ fontSize: 17, fontFamily: 'var(--mono)' }}>
            {value}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
